// oxlint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Component } from 'react';
import { defaultProps } from './props';
import type { BaseReactPlayerProps } from './types';
import { isEqual } from './utils';

const SEEK_ON_PLAY_EXPIRY = 5000;
const DOCUMENT_EVENTS = ['click', 'keydown', 'touchstart', 'touchend'];

export default class Player extends Component<BaseReactPlayerProps> {
  static defaultProps = defaultProps;

  player: Player | null = null;

  mounted = false;

  isReady = false;

  isPlaying = false;

  // Track playing state internally to prevent bugs
  isLoading = true;

  isPageClosing = false;

  // Use isLoading to prevent onPause when switching URL
  loadOnReady = null;

  startOnPlay = true;

  seekOnPlay = null;

  onDurationCalled = false;

  eventCalled = false;

  handleInteraction = () => {
    DOCUMENT_EVENTS.forEach((event) => {
      document.removeEventListener(event, this.handleInteraction);
    });

    const {
      autoplay,
      storage,
      saveProgress,
      displayDeactivation,
      onInteracted
    } = this.props;

    onInteracted?.();

    const lastAction = saveProgress ? storage?.get() : undefined;

    const shouldAutoplay =
      !displayDeactivation &&
      ((autoplay && !lastAction) || lastAction === 'play');

    if (shouldAutoplay && !this.eventCalled && !this.isPlaying) {
      this.player?.play();
      this.eventCalled = true;
    }
  };

  componentDidMount() {
    this.mounted = true;
    this.player.load(this.props.url);
    this.progress();

    window.addEventListener('beforeunload', this.handlePageClosing);
    DOCUMENT_EVENTS.forEach((event) => {
      document.addEventListener(event, this.handleInteraction);
    });
  }

  componentDidUpdate(prevProps) {
    // Invoke player methods based on changed props
    const { url, playing, pip, volume, muted, playbackRate, loop } = this.props;
    const {
      url: prevUrl,
      playing: prevPlaying,
      pip: prevPip,
      volume: prevVolume,
      muted: prevMuted,
      playbackRate: prevPlaybackRate,
      loop: prevLoop
    } = prevProps;

    if (!isEqual(prevUrl, url)) {
      this.isLoading = true;
      this.startOnPlay = true;
      this.onDurationCalled = false;

      this.player.load(url, this.isReady);
    }

    if (!prevPlaying && playing) {
      this.player.play();
    }

    if (prevPlaying && !playing) {
      this.player.pause();
    }

    if (!prevPlaying && !playing) {
      this.handleDurationCheck();
    }

    if (!prevPip && pip && this.player.enablePIP) {
      this.player.enablePIP();
    }

    if (prevPip && !pip && this.player.disablePIP) {
      this.player.disablePIP();
    }

    if (prevVolume !== volume && volume !== null) {
      this.player.setVolume(volume);
    }

    if (prevMuted !== muted) {
      if (muted) {
        this.player.mute();
      } else {
        this.player.unmute();
        if (volume !== null) {
          // Set volume next tick to fix a bug with DailyMotion
          setTimeout(() => this.player.setVolume(volume));
        }
      }
    }

    if (prevPlaybackRate !== playbackRate && this.player.setPlaybackRate) {
      this.player.setPlaybackRate(playbackRate);
    }

    if (prevLoop !== loop && this.player.setLoop) {
      this.player.setLoop(loop);
    }
  }

  handlePageClosing = () => {
    this.isPageClosing = true;
  };

  handlePlayerMount = (player) => {
    if (this.player) {
      this.progress(); // Ensure onProgress is still called in strict mode
      return; // Return here to prevent loading twice in strict mode
    }
    this.player = player;
    this.player.load(this.props.url);
    this.progress();
  };

  componentWillUnmount() {
    this.mounted = false;

    clearTimeout(this.progressTimeout);
    clearTimeout(this.durationCheckTimeout);

    if (this.isReady) {
      this.player.stop();
    }

    if (this.player.disablePIP) {
      this.player.disablePIP();
    }

    window.removeEventListener('beforeunload', this.handlePageClosing);
    DOCUMENT_EVENTS.forEach((event) => {
      document.removeEventListener(event, this.handleInteraction);
    });
  }

  getDuration() {
    if (!this.isReady) return null;
    return this.player.getDuration();
  }

  getCurrentTime() {
    if (!this.isReady) return null;
    return this.player.getCurrentTime();
  }

  getSecondsLoaded() {
    if (!this.isReady) return null;
    return this.player.getSecondsLoaded();
  }

  getInternalPlayer = (key) => {
    if (!this.player) return null;
    return this.player[key];
  };

  progress = () => {
    const { url, onProgress, progressFrequency, progressInterval } = this.props;

    if (url && this.player && this.isReady) {
      const playedSeconds = this.getCurrentTime() || 0;
      const loadedSeconds = this.getSecondsLoaded();
      const duration = this.getDuration();

      if (duration) {
        const progress = {
          playedSeconds,
          played: playedSeconds / duration
        };

        if (loadedSeconds !== null) {
          progress.loadedSeconds = loadedSeconds;
          progress.loaded = loadedSeconds / duration;
        }

        // Only call onProgress if values have changed
        if (
          progress.playedSeconds !== this.prevPlayed ||
          progress.loadedSeconds !== this.prevLoaded
        ) {
          onProgress(progress);
        }

        this.prevPlayed = progress.playedSeconds;
        this.prevLoaded = progress.loadedSeconds;
      }
    }

    this.progressTimeout = setTimeout(
      this.progress,
      progressFrequency || progressInterval
    );
  };

  handleReady = () => {
    if (!this.mounted) return;

    this.isReady = true;
    this.isLoading = false;

    const { onReady, playing, volume, muted, storage } = this.props;
    const lastAction = storage?.get();

    onReady();

    if (!muted && volume !== null) {
      this.player.setVolume(volume);
    }

    if (this.loadOnReady) {
      this.player.load(this.loadOnReady, true);
      // this.player.load(this.loadOnReady); // fix players change
      this.loadOnReady = null;
    } else if (playing || lastAction === 'play') {
      this.player.play();
    }

    this.handleDurationCheck();
  };

  handlePlay = () => {
    const {
      onStart,
      onPlay,
      onTogglePlaying,
      hasInteracted,
      playbackRate,
      storage
    } = this.props;

    if (!hasInteracted) {
      return;
    }

    this.handleInteraction();
    this.isPlaying = true;
    this.isLoading = false;

    if (this.startOnPlay) {
      if (this.player.setPlaybackRate && playbackRate !== 1) {
        this.player.setPlaybackRate(playbackRate);
      }
      onStart();
      this.startOnPlay = false;
    }

    onPlay();

    if (this.seekOnPlay) {
      this.seekTo(this.seekOnPlay);
      this.seekOnPlay = null;
    }

    onTogglePlaying(true);
    storage?.set('play');
    this.handleDurationCheck();
  };

  handlePause = (e) => {
    if (!this.mounted || this.isPageClosing || !this.props.hasInteracted) {
      return;
    }

    const { onPause, storage } = this.props;
    this.isPlaying = false;
    if (!this.isLoading) {
      onPause(e);
      storage?.set('pause');
    }
  };

  handleEnded = () => {
    const { activePlayer, loop, onEnded, storage } = this.props;

    onEnded();

    if (activePlayer.loopOnEnded && loop) {
      this.seekTo(0);
    }

    if (!loop) {
      this.isPlaying = false;
      storage?.set('pause');
    }
  };

  handleError = (...args) => {
    const { onError } = this.props;
    this.isLoading = false;
    onError(...args);
  };

  handleDurationCheck = () => {
    const { onDuration } = this.props;

    clearTimeout(this.durationCheckTimeout);
    const duration = this.getDuration();

    if (duration) {
      if (!this.onDurationCalled) {
        onDuration(duration);
        this.onDurationCalled = true;
      }
    } else {
      this.durationCheckTimeout = setTimeout(this.handleDurationCheck, 100);
    }
  };

  handleLoaded = () => {
    // Sometimes we know loading has stopped but onReady/onPlay are never called
    // so this provides a way for players to avoid getting stuck
    this.isLoading = false;
  };

  ref = (player) => {
    if (player) {
      this.player = player;
    }
  };

  seekTo(amount, type) {
    // When seeking before player is ready, store value and seek later
    if (!this.isReady && amount !== 0) {
      this.seekOnPlay = amount;

      setTimeout(() => {
        this.seekOnPlay = null;
      }, SEEK_ON_PLAY_EXPIRY);

      return;
    }

    const isFraction = !type ? amount > 0 && amount < 1 : type === 'fraction';

    if (isFraction) {
      // Convert fraction to seconds based on duration
      const duration = this.player.getDuration();
      if (!duration) {
        console.warn(
          'ReactPlayer: could not seek using fraction – duration not yet available'
        );
        return;
      }

      this.player.seekTo(duration * amount);

      return;
    }

    this.player.seekTo(amount);
  }

  render() {
    const { activePlayer: PlayerElement } = this.props;

    if (!Player) {
      return null;
    }

    return (
      <PlayerElement
        {...this.props}
        ref={this.ref}
        onMount={this.handlePlayerMount}
        onReady={this.handleReady}
        onPlay={this.handlePlay}
        onPause={this.handlePause}
        onEnded={this.handleEnded}
        onLoaded={this.handleLoaded}
        onError={this.handleError}
      />
    );
  }
}
